// Shared plan and tier configuration.
//
// Must match api/app/core/plans.py — the API is the authority for every write
// (plan, tier, features, storage all go through it); this file exists so the
// navigation can gate off the live subscriber snapshot without a round trip.
//
// Two orthogonal axes describe a subscription:
//   plan  — the storage size: trial (5GB), plan_100gb, plan_500gb, extended
//           in 100GB blocks through `additional_storage` (bytes).
//   tier  — 'basic' | 'pro' — what the workspace is *entitled* to.
//
// A subscriber document written before tiers existed carries no `tier` and is
// read as Basic (DEFAULT_TIER_FOR_LEGACY, product decision 2026-09-10): its
// stored flags stay as they are, but nothing Pro is shown or allowed until a
// system admin sets the tier.

const GB = 1024 * 1024 * 1024;

export const PLAN_STORAGE = {
  'trial': 5 * GB,
  'plan_100gb': 100 * GB,
  'plan_500gb': 500 * GB,
};

export const PLAN_NAMES = {
  'trial': 'Trial (5GB)',
  'plan_100gb': '100GB Plan',
  'plan_500gb': '500GB Plan',
};

/** Dropdown options in display order: trial, 100GB, 500GB. */
// Old plan ids still on some documents, as the API maps them
// (LEGACY_PLAN_ALIASES in api/app/core/plans.py).
const LEGACY_PLAN_ALIASES = { trial: 'trial', plan_basic: 'plan_100gb', standard: 'plan_100gb' };

/** The canonical plan id, as the API's normalise_plan: unknown reads as trial. */
export const normalisePlan = (plan) => {
  if (!plan) return 'trial';
  const key = String(plan).trim();
  if (PLAN_STORAGE[key] !== undefined) return key;
  return LEGACY_PLAN_ALIASES[key.toLowerCase()] || 'trial';
};

export const PLAN_OPTIONS = Object.keys(PLAN_STORAGE).map((id) => ({ id, label: PLAN_NAMES[id] }));

export const calculateTotalStorage = (plan, additionalStorage = 0) => {
  const baseStorage = PLAN_STORAGE[plan] || PLAN_STORAGE['trial'];
  return baseStorage + additionalStorage;
};

// ---------------------------------------------------------------------------
// Tiers
// ---------------------------------------------------------------------------

export const DEFAULT_TIER_FOR_LEGACY = 'basic';

/** Flags a workspace admin may switch on and off (Settings → Features), within the tier. */
export const TOGGLEABLE_FEATURES = ['asset_tracking', 'issue_tracking'];
/** Entitlements with no per-workspace flag: the tier alone decides. */
export const TIER_ONLY_FEATURES = ['assignments', 'integrations', 'mass_export', 'sso', 'customer_portal', 'custom_templates'];
/** Flags the system admin sets per workspace, still bounded by the tier. */
export const ADMIN_FEATURES = ['advanced_analytics', 'api_access', 'mcp_access', 'full_text_search'];
/** Every per-workspace flag a tier governs (FLAG_FEATURES in plans.py). */
export const FLAG_FEATURES = [...TOGGLEABLE_FEATURES, ...ADMIN_FEATURES];

export const TIERS = {
  basic: {
    id: 'basic',
    name: 'Basic',
    entitlements: {
      asset_tracking: false,
      issue_tracking: false,
      assignments: true,
      integrations: false,
      api_access: false,
      mcp_access: false,
      mass_export: true,
      advanced_analytics: false,
      sso: false,
      customer_portal: true,
      custom_templates: true,
      full_text_search: true,
    },
    scheduledReports: { maxDaily: 3, maxWeekly: 3, maxRecipients: 5 },
    downloadAllowanceRatio: 0.5,
    priceAnnual: { plan_100gb: 2000, plan_500gb: 4000 },
    addon100gbPrice: 600,
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    entitlements: {
      asset_tracking: true,
      issue_tracking: true,
      assignments: true,
      integrations: true,
      api_access: true,
      mcp_access: true,
      mass_export: true,
      advanced_analytics: true,
      sso: true,
      customer_portal: true,
      custom_templates: true,
      full_text_search: true,
    },
    // null = unlimited
    scheduledReports: { maxDaily: null, maxWeekly: null, maxRecipients: 20 },
    downloadAllowanceRatio: 1.0,
    priceAnnual: { plan_100gb: 3000, plan_500gb: 6000 },
    addon100gbPrice: 900,
  },
};

export const TIER_OPTIONS = Object.values(TIERS).map(({ id, name }) => ({ id, label: name }));

/** Canonical tier id; anything unrecognised reads as the legacy default. */
export const normaliseTier = (value, fallback = DEFAULT_TIER_FOR_LEGACY) => {
  if (typeof value === 'string' && TIERS[value.trim().toLowerCase()]) {
    return value.trim().toLowerCase();
  }
  return fallback;
};

/** The tier a subscriber document is on, applying the legacy default. */
export const subscriberTier = (data) => normaliseTier(data?.tier);

export const tierName = (tier) => TIERS[normaliseTier(tier)].name;

/**
 * May `feature` be on for a workspace on `tier`? Features the table does not
 * govern (blur_detection, task_management, anything not yet classified) return
 * true — the tier only ever restricts.
 */
export const tierAllows = (tier, feature) => {
  const entitlements = TIERS[normaliseTier(tier)].entitlements;
  if (Object.prototype.hasOwnProperty.call(entitlements, feature)) {
    return entitlements[feature] === true;
  }
  return true;
};

/**
 * A copy of `features` with every tier-governed flag forced off where the tier
 * does not allow it. Ungoverned keys pass through untouched, so a workspace's
 * blur_detection choice survives a downgrade.
 */
export const clampFeatures = (tier, features) => {
  const out = {};
  Object.entries(features || {}).forEach(([key, value]) => {
    out[key] = value === true && tierAllows(tier, key);
  });
  return out;
};

/**
 * The feature switches after the system admin moves a workspace from
 * `fromTier` to `toTier`. A move to Pro ticks every flag Pro allows (product
 * owner, 2026-09-24); any move clamps, so a move to Basic unticks what Basic
 * does not include. The same tier again only clamps, so re-selecting Pro
 * never turns back on a switch the admin had turned off. Mirrors the rule in
 * `apply_entitlements` (api/app/services/subscriber_service.py).
 */
export const featuresForTierChange = (fromTier, toTier, features) => {
  const from = normaliseTier(fromTier);
  const to = normaliseTier(toTier);
  const next = { ...(features || {}) };
  if (to === 'pro' && from !== 'pro') {
    FLAG_FEATURES.forEach((feature) => {
      if (tierAllows(to, feature)) next[feature] = true;
    });
  }
  return clampFeatures(to, next);
};

/**
 * Everything a client gates on, in one map: the clamped flags plus the
 * tier-only entitlements (which have no flag of their own).
 */
export const effectiveFeatures = (data) => {
  const tier = subscriberTier(data);
  const out = clampFeatures(tier, data?.features);
  TIER_ONLY_FEATURES.forEach((feature) => { out[feature] = tierAllows(tier, feature); });
  out.task_management = true;
  return out;
};

/** `{ maxDaily, maxWeekly, maxRecipients }`; null = unlimited. */
export const scheduledReportLimits = (tier) => ({ ...TIERS[normaliseTier(tier)].scheduledReports });

// ---------------------------------------------------------------------------
// Pro trial for a Basic workspace
// ---------------------------------------------------------------------------
//
// A paying Basic workspace can have everything in Pro for 30 days inside the
// subscription it already has (product owner, 2026-10-06). The API moves
// `tier` to 'pro' for the period and keeps a `pro_trial` record on the
// subscriber document; an hourly job moves it back. So during a trial every
// gate above already reads Pro and nothing here has to special-case it. What
// this section answers is the other question: is the Pro this workspace has
// borrowed, and until when. Mirrors PRO_TRIAL_DAYS / PRO_TRIAL_COOLDOWN_DAYS
// and `trial_state` in api/app/services/pro_trial_service.py.

export const PRO_TRIAL_DAYS = 30;
/** A workspace admin may start one trial in this many days, counted from the last start. */
export const PRO_TRIAL_COOLDOWN_DAYS = 365;

const DAY_MS = 24 * 60 * 60 * 1000;

/** A Firestore Timestamp, a Date or an ISO string as a Date; anything else is null. */
export const asDate = (value) => {
  if (!value) return null;
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Where a workspace stands on the Pro trial.
 *   active           a trial is running; `endsAt` and `daysLeft` say until when
 *   canSelfStart     a workspace admin may start one now
 *   nextSelfStartAt  when they next may, if the yearly wait is what stops them
 * A system admin is not bound by the wait; the API decides, this only shows.
 */
export const proTrialState = (data, now = new Date()) => {
  const record = data?.pro_trial || {};
  const active = record.active === true;
  const startedAt = asDate(record.started_at);
  const endsAt = asDate(record.ends_at);
  const nextAt = startedAt ? new Date(startedAt.getTime() + PRO_TRIAL_COOLDOWN_DAYS * DAY_MS) : null;
  // Normalised as the API does: 'Trial', the id StartTrialForm once wrote,
  // is the trial plan, which has no Pro trial to offer (#977).
  const onBasic = subscriberTier(data) === 'basic' && normalisePlan(data?.plan) !== 'trial';
  const canSelfStart = onBasic && !active && (!nextAt || nextAt <= now);
  return {
    active,
    endsAt: active ? endsAt : null,
    // Whole days, rounded up: "1 day left" until the hour it ends.
    daysLeft: active && endsAt ? Math.max(0, Math.ceil((endsAt - now) / DAY_MS)) : null,
    canSelfStart,
    nextSelfStartAt: onBasic && !active && !canSelfStart ? nextAt : null,
  };
};

/** The tier a workspace pays for: Basic while a Pro trial is running. */
export const paidTier = (data) => (data?.pro_trial?.active === true ? 'basic' : subscriberTier(data));

// ---------------------------------------------------------------------------
// Upgrade contact
// ---------------------------------------------------------------------------

// A workspace reaches TrakLog through <UpgradeContactLink>, which opens a form
// the server forwards (POST /api/v1/contact/workspace). There is no address
// here, and none in any page: it is not published (product owner, 2026-10-07).
// api/tests/test_contact_address_hidden.py fails if one comes back.
